import React from 'react';

import axios, { CancelTokenSource } from 'axios';

import Logger from 'utils/logger';
import { canScroll, isNearBottom } from 'utils/scroll';

import API from 'utils/api';

import { MemberGroupHistoryRow } from 'utils/api/modules/memberGroups';

import { batchSizeOf, memberPreview } from './history';

export const BATCH_MEMBERS_PAGE_SIZE = 100;

export type BatchMembers = {
  names: string[];
  hasMore: boolean;
  needsButton: boolean;
  loading: boolean;
  failed: boolean;
  loadMore: () => void;
  onScroll: (event: React.UIEvent<HTMLElement>) => void;
  listRef: (node: HTMLElement | null) => void;
};

/**
 * The View-members list for one batch.
 */
export const useBatchMembers = (groupUuid: string | undefined, entry: MemberGroupHistoryRow): BatchMembers => {
  const preview = memberPreview(entry);
  const [extra, setExtra] = React.useState<(string | null)[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [failed, setFailed] = React.useState(false);
  const [exhausted, setExhausted] = React.useState(false);

  const loaded = preview.length + extra.length;
  const total = batchSizeOf(entry);

  const inFlight = React.useRef(false);
  const cancelSource = React.useRef<CancelTokenSource | null>(null);
  const currentSource = (): CancelTokenSource => {
    if (!cancelSource.current) {
      cancelSource.current = axios.CancelToken.source();
    }
    return cancelSource.current;
  };

  React.useEffect(
    () => () => {
      cancelSource.current?.cancel();
    },
    []
  );

  const loadMore = React.useCallback(async () => {
    if (!groupUuid || inFlight.current) {
      return;
    }
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    try {
      const { data } = await API.memberGroups.listHistoryMembers(
        groupUuid,
        entry.uuid,
        { limit: BATCH_MEMBERS_PAGE_SIZE, offset: loaded },
        currentSource()
      )();
      if (data.next === null || data.results.length === 0) {
        setExhausted(true);
      }
      setExtra((current) => [...current, ...data.results.map((member) => member?.name ?? null)]);
    } catch (ex) {
      if (!axios.isCancel(ex)) {
        Logger.error(`Invalid member group history members API response. Entry id: ${entry.uuid}`, ex);
        setFailed(true);
      }
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [groupUuid, entry.uuid, loaded]);

  const hasMore = !exhausted && loaded < total;

  const onScroll = React.useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (hasMore && !failed && isNearBottom(event.currentTarget)) {
        loadMore();
      }
    },
    [hasMore, failed, loadMore]
  );

  const [scrollable, setScrollable] = React.useState(true);
  const listRef = React.useCallback((node: HTMLElement | null) => {
    if (node) {
      setScrollable(canScroll(node));
    }
  }, []);

  return {
    names: [...preview, ...extra].map((name) => name ?? 'Someone'),
    hasMore,
    needsButton: !scrollable,
    loading,
    listRef,
    failed,
    loadMore,
    onScroll,
  };
};
